/**
 * Iframe/WebView embedding utilities.
 *
 * Supports:
 * - Token-based auth via URL query param: ?token=XXX
 * - postMessage communication with parent window
 * - Detection of embedded context (iframe or WebView)
 */

import { setAuth, clearAuth, type CustomerUserInfo } from './auth';
import { setEmbedRequesterContext, type EmbedRequesterContext } from './auth';

/** Returns true when the portal is running inside an iframe */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // cross-origin iframe will throw — that also counts as embedded
    return true;
  }
}

/**
 * Extract token from URL query string (?token=XXX) and persist it.
 * Returns the token if found, null otherwise.
 */
export function extractTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

// --- postMessage protocol ---

export type EmbedMessageType =
  | 'helpdesk:ready'
  | 'helpdesk:auth:success'
  | 'helpdesk:auth:logout'
  | 'helpdesk:badge:status'
  | 'helpdesk:navigate'
  | 'helpdesk:ticket:created'
  | 'helpdesk:ticket:updated'
  | 'helpdesk:resize';

export interface EmbedMessage {
  type: EmbedMessageType;
  payload?: unknown;
}

/** Send a message to the parent window (no-op if not embedded) */
export function postToParent(type: EmbedMessageType, payload?: unknown): void {
  if (!isEmbedded()) return;
  try {
    window.parent.postMessage({ type, payload }, '*');
  } catch {
    // silently fail if parent is unreachable
  }
}

/** Notify parent that the portal is ready */
export function notifyReady(): void {
  postToParent('helpdesk:ready');
}

/** Notify parent of successful authentication */
export function notifyAuthSuccess(user: CustomerUserInfo): void {
  postToParent('helpdesk:auth:success', { user });
}

/** Notify parent of logout */
export function notifyLogout(): void {
  postToParent('helpdesk:auth:logout');
}

/** Notify parent of embedded badge status */
export function notifyBadgeStatus(payload: unknown): void {
  postToParent('helpdesk:badge:status', payload);
}

/** Notify parent that a ticket was created */
export function notifyTicketCreated(ticketId: string): void {
  postToParent('helpdesk:ticket:created', { ticketId });
}

/** Notify parent that a ticket was updated */
export function notifyTicketUpdated(ticketId: string): void {
  postToParent('helpdesk:ticket:updated', { ticketId });
}

/** Notify parent of current content height (for auto-resize) */
export function notifyResize(): void {
  postToParent('helpdesk:resize', {
    height: document.documentElement.scrollHeight,
  });
}

// --- Inbound messages from parent ---

type InboundHandler = (payload: unknown) => void;
const handlers = new Map<string, InboundHandler>();

interface EmbeddedAuthPayload {
  accessToken: string;
  refreshToken: string;
  user: CustomerUserInfo;
  requesterContext?: EmbedRequesterContext;
  defaultRoute?: string;
}

function normalizeEmbeddedUser(user: Partial<CustomerUserInfo> | undefined): CustomerUserInfo | null {
  if (!user?.id || !user.email || !user.fullName || !user.organizationId) {
    return null;
  }

  return {
    id: user.id,
    email: user.email,
    fullName: user.fullName,
    organizationId: user.organizationId,
    projectIds: Array.isArray(user.projectIds) ? user.projectIds : [],
  };
}

function normalizeAuthPayload(payload: unknown): EmbeddedAuthPayload | null {
  const candidate = payload as
    | {
        accessToken?: string;
        refreshToken?: string;
        user?: Partial<CustomerUserInfo>;
        requesterContext?: EmbedRequesterContext;
        defaultRoute?: string;
        tokens?: {
          accessToken?: string;
          refreshToken?: string;
        };
      }
    | undefined;

  if (!candidate) {
    return null;
  }

  const accessToken = candidate.accessToken ?? candidate.tokens?.accessToken;
  const refreshToken = candidate.refreshToken ?? candidate.tokens?.refreshToken;
  const user = normalizeEmbeddedUser(candidate.user);

  if (!accessToken || !refreshToken || !user) {
    return null;
  }

  return {
    accessToken,
    refreshToken,
    user,
    requesterContext: candidate.requesterContext,
    defaultRoute: candidate.defaultRoute,
  };
}

export function onParentMessage(type: string, handler: InboundHandler): () => void {
  handlers.set(type, handler);
  return () => { handlers.delete(type); };
}

function handleMessage(event: MessageEvent) {
  const data = event.data as EmbedMessage | undefined;
  if (!data || typeof data.type !== 'string') return;
  const handler = handlers.get(data.type);
  if (handler) handler(data.payload);
}

/** Initialise embed listeners. Call once on app mount. */
export function initEmbed(): void {
  if (!isEmbedded()) return;
  window.addEventListener('message', handleMessage);

  // Handle token injection from parent
  onParentMessage('helpdesk:auth:setToken', (payload) => {
    const p = normalizeAuthPayload(payload);
    if (p) {
      setAuth(p.accessToken, p.refreshToken, p.user);
      setEmbedRequesterContext(p.requesterContext ?? null);
      if (typeof p.defaultRoute === 'string' && p.defaultRoute.trim()) {
        const nextRoute = p.defaultRoute.startsWith('/') ? p.defaultRoute : `/${p.defaultRoute}`;
        if (isEmbedded()) {
          const nextHash = `#${nextRoute}`;
          if (window.location.hash !== nextHash) {
            window.location.replace(`${window.location.pathname}${window.location.search}${nextHash}`);
            return;
          }
        } else if (window.location.pathname !== nextRoute) {
          window.location.replace(nextRoute);
          return;
        }
      }
    }
  });

  // Handle logout command from parent
  onParentMessage('helpdesk:auth:clearToken', () => {
    clearAuth();
    window.location.reload();
  });

  // Send resize events on content changes
  const observer = new ResizeObserver(() => notifyResize());
  observer.observe(document.documentElement);

  notifyReady();
}
